import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useGetDispatch } from "../../../utils/react-query/queryDispatches/queries";
import { statusLabels } from "../statusLabels";
import type { DispatchMethod } from "../types";

interface DispatchDetailProps {
  dispatchId: number | null;
  onClose: () => void;
}

const methodLabels: Record<DispatchMethod, string> = {
  pickup: "Recogida",
  delivery: "Entrega",
  courier: "Courier",
};

function formatDate(value: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-CO");
}

export function DispatchDetail({ dispatchId, onClose }: DispatchDetailProps) {
  const { data, isLoading } = useGetDispatch(dispatchId ?? 0);

  if (dispatchId == null) return null;

  const dispatch = data?.dispatch ?? null;
  const items = data?.items ?? dispatch?.items ?? [];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        disableTypography
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">Detalle del despacho #{dispatchId}</Typography>
        <IconButton size="small" onClick={onClose} aria-label="cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "2rem",
            }}
          >
            <CircularProgress size={32} />
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginLeft: 12, alignSelf: "center" }}
            >
              Cargando despacho...
            </Typography>
          </div>
        )}

        {!isLoading && !dispatch && (
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            style={{ padding: "2rem" }}
          >
            No se encontró el despacho.
          </Typography>
        )}

        {!isLoading && dispatch && (
          <>
            <Typography variant="body2">
              <strong>Asesor: </strong>
              {(dispatch as any).created_by_name ||
                String(dispatch.created_by ?? "—")}
            </Typography>
            <Typography variant="body2">
              <strong>F. Solicitud: </strong>
              {formatDate(dispatch.created_at)}
            </Typography>
            <Typography variant="body2">
              <strong>Cotización: </strong>
              {dispatch.reservation?.quote_number ||
                (dispatch as any).quote_number ||
                "—"}
            </Typography>
            <Typography variant="body2">
              <strong>Estado: </strong>
              {statusLabels[dispatch.status] ?? dispatch.status}
            </Typography>
            <Typography variant="body2">
              <strong>Cliente: </strong>
              {dispatch.recipient_name}
            </Typography>
            <Typography variant="body2">
              <strong>Ciudad: </strong>
              {dispatch.delivery_address || "—"}
            </Typography>
            <Typography variant="body2">
              <strong>Observación: </strong>
              {dispatch.notes || "—"}
            </Typography>
            <Typography variant="body2">
              <strong>Rutero: </strong>
              {dispatch.route || "—"}
            </Typography>
            <Typography variant="body2">
              <strong>Dirección: </strong>
              {[dispatch.delivery_address, dispatch.recipient_phone]
                .filter(Boolean)
                .join(" — ") || "—"}
            </Typography>
            <Typography variant="body2">
              <strong>F. Despacho: </strong>
              {formatDate(dispatch.dispatch_date)}
            </Typography>
            <Typography variant="body2">
              <strong>Guía: </strong>
              {dispatch.tracking_guide || "—"}
            </Typography>
            <Typography variant="body2">
              <strong>Remisión: </strong>
              {dispatch.remission_code || "—"}
            </Typography>
            <Typography variant="body2">
              <strong>Factura: </strong>
              {dispatch.invoice_number || "—"}
            </Typography>
            <Typography variant="body2">
              <strong>Convención: </strong>
              {dispatch.convention || "—"}
            </Typography>
            <Typography variant="body2">
              <strong>F. Aprobación: </strong>
              {dispatch.approved_at
                ? `${formatDate(dispatch.approved_at)}${
                    dispatch.approved_by ? ` (${dispatch.approved_by})` : ""
                  }`
                : "—"}
            </Typography>
            <Typography variant="body2">
              <strong>Duración: </strong>
              {dispatch.duration_days != null
                ? String(dispatch.duration_days)
                : "—"}
            </Typography>
            <Typography variant="body2">
              <strong>Tipo Entrega: </strong>
              {methodLabels[dispatch.method] ?? dispatch.method}
            </Typography>

            <Typography variant="subtitle1" style={{ marginTop: 16 }}>
              Productos
            </Typography>

            {items.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                Sin productos registrados.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell>Dirección/Bodega</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {item.product_name || `Producto #${item.product_id}`}
                      </TableCell>
                      <TableCell>
                        {item.address_name ||
                          `Bodega #${item.storage_address_id}`}
                      </TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DispatchDetail;
